import { useEffect } from 'react'
import { useStatusStore } from '@/store/status'

/** Minimal EventSource surface we depend on — lets tests inject a mock. */
export interface EventSourceLike {
  addEventListener(type: string, listener: (ev: MessageEvent) => void): void
  onopen: ((ev: Event) => void) | null
  onerror: ((ev: Event) => void) | null
  readyState: number
  close(): void
}

/** The store actions the stream drives (subset of the status store). */
export interface StreamStore {
  setConnecting(): void
  markOpen(): void
  markClosed(): void
  applySnapshot(systems: unknown): void
  applyUpdate(
    system: string,
    msg: { status?: unknown; updated_at?: string | null; payload?: unknown },
  ): void
}

const CLOSED = 2 // EventSource.CLOSED — reached only on a fatal (non-reconnecting) error.
const RETRY_MIN_MS = 5_000
const RETRY_MAX_MS = 60_000

function parse(data: unknown): Record<string, unknown> | null {
  try {
    const o = JSON.parse(typeof data === 'string' ? data : '')
    return o && typeof o === 'object' ? (o as Record<string, unknown>) : null
  } catch {
    return null // malformed frame → ignore, never throw
  }
}

/**
 * Wire a (mockable) EventSource to the status store; returns a teardown fn.
 * `onFatal` runs when the browser gives the stream up for good.
 *
 * - Uses named events (`addEventListener`), not `onmessage`.
 * - The server resends `initial_snapshot` on every (re)connect, so state
 *   self-heals — we just re-apply it each time.
 * - EventSource reconnects a transient drop itself; here we only mirror
 *   connection state so the UI can show a "live / reconnecting" indicator.
 * - `: ping` heartbeat comments are ignored by EventSource automatically.
 */
export function attachStatusStream(
  es: EventSourceLike,
  store: StreamStore,
  onFatal?: () => void,
): () => void {
  store.setConnecting()

  es.onopen = () => store.markOpen()

  es.onerror = () => {
    // CLOSED = fatal (won't retry); anything else = transient drop → reconnecting.
    if (es.readyState === CLOSED) {
      store.markClosed()
      onFatal?.()
    } else store.setConnecting()
  }

  es.addEventListener('initial_snapshot', (ev) => {
    const msg = parse(ev.data)
    if (msg) store.applySnapshot(msg.systems)
  })

  es.addEventListener('status_update', (ev) => {
    const msg = parse(ev.data)
    if (msg) store.applyUpdate(String(msg.system ?? ''), msg)
  })

  return () => es.close()
}

/**
 * Receive-only health-status SSE client (browser-native EventSource).
 * Opens for the app's lifetime and closes on teardown to avoid leaked
 * connections. EventSource retries a dropped connection itself, but a non-200
 * answer ends it for good, and nginx returns 502 for the seconds a deploy
 * restarts the API. Without a fresh EventSource the tab read "Reconnecting…"
 * until someone reloaded it.
 */
export function useStatusStream(url: string, enabled = true) {
  useEffect(() => {
    if (!enabled) return
    const store = useStatusStore.getState()
    let detach: (() => void) | undefined
    let retry: ReturnType<typeof setTimeout> | undefined
    let delay = RETRY_MIN_MS

    const connect = () => {
      let es: EventSource
      try {
        es = new EventSource(url)
      } catch {
        store.markClosed()
        return
      }
      es.addEventListener('open', () => (delay = RETRY_MIN_MS))
      detach = attachStatusStream(es as unknown as EventSourceLike, store, () => {
        detach?.()
        retry = setTimeout(connect, delay)
        delay = Math.min(delay * 2, RETRY_MAX_MS)
      })
    }

    connect()
    return () => {
      clearTimeout(retry)
      detach?.()
    }
  }, [url, enabled])
}
